/**
 * video-analyzer-engine.js — pure (DOM-free) interpretation layer for the
 * /analyze video-analyzer etalon.
 *
 * Takes the raw probes (mediainfo-probe grouped tracks + bitrate timeline +
 * per-track loudness) and turns them into:
 *   - a normalized, render-ready `report` (one clean shape, parsed numbers)
 *   - honest, evidence-backed verdicts: platform-readiness (YouTube /
 *     Instagram / TikTok / broadcast / HLS), encoding-quality, HDR/color
 *     consistency.
 *
 * Design rules:
 *   - No `window`/DOM use → unit-testable in node (see selfValidate()).
 *   - Every verdict carries the EVIDENCE (which field/number fired) and the
 *     spec it checks. Ambiguous cases are flagged 'warn', never a false pass.
 *   - Nothing here promises where data goes — it only describes the file.
 *
 * Public API (window.VideoAnalyzerEngine, also module.exports under node):
 *   buildReport({ mi, timeline, fileName, fileSize, audio, webcodecs }) -> report
 *   platformVerdicts(report) -> [{ platform, status, issues:[], note }]
 *   encodingVerdict(report)  -> { status, label, reasons:[] }
 *   colorHdrChecks(report)   -> [{ level, msg }]
 *   summary(report)          -> { headline, badges:[], status }
 *   selfValidate()           -> { passed, total, failures:[] }
 */
(function(root){
'use strict';

/* ── field access (tolerant to MediaInfo's spelling variants) ── */
function field(track /* ...names */) {
    if (!track) return '';
    for (var i = 1; i < arguments.length; i++) {
        var k = arguments[i];
        if (track[k] != null && track[k] !== '') return String(track[k]);
        for (var real in track) {
            if (Object.prototype.hasOwnProperty.call(track, real) && real.toLowerCase() === k.toLowerCase()) {
                if (track[real] != null && track[real] !== '') return String(track[real]);
            }
        }
    }
    return '';
}
function num(v) {
    if (v == null) return NaN;
    var s = String(v).replace(/ /g, ' ').replace(/\s+/g, '').replace(',', '.');
    var m = s.match(/-?\d+(\.\d+)?/);
    return m ? parseFloat(m[0]) : NaN;
}

/* ── codec normalization ── */
function videoCodecLabel(fmt, codecId) {
    var f = (fmt || '').toLowerCase(), c = (codecId || '').toLowerCase();
    if (f.indexOf('avc') >= 0 || c.indexOf('avc1') >= 0 || f === 'h.264' || c.indexOf('h264') >= 0) return 'H.264 / AVC';
    if (f.indexOf('hevc') >= 0 || f.indexOf('h.265') >= 0 || c.indexOf('hvc1') >= 0 || c.indexOf('hev1') >= 0) return 'HEVC / H.265';
    if (f.indexOf('av1') >= 0 || c.indexOf('av01') >= 0) return 'AV1';
    if (f.indexOf('vp9') >= 0 || c.indexOf('vp09') >= 0) return 'VP9';
    if (f.indexOf('vp8') >= 0) return 'VP8';
    if (f.indexOf('prores') >= 0) return 'Apple ProRes';
    if (f.indexOf('dnxhd') >= 0 || f.indexOf('vc-3') >= 0) return 'DNxHD/HR';
    if (f.indexOf('mpeg-2') >= 0 || f.indexOf('mpeg2') >= 0) return 'MPEG-2';
    if (f.indexOf('mpeg-4 visual') >= 0 || f.indexOf('xvid') >= 0 || f.indexOf('divx') >= 0) return 'MPEG-4 Visual';
    if (f.indexOf('vvc') >= 0 || f.indexOf('h.266') >= 0) return 'VVC / H.266';
    return fmt || codecId || '—';
}
function videoCodecKey(fmt, codecId) {
    var l = videoCodecLabel(fmt, codecId);
    if (l.indexOf('H.264') >= 0) return 'h264';
    if (l.indexOf('HEVC') >= 0) return 'hevc';
    if (l === 'AV1') return 'av1';
    if (l === 'VP9') return 'vp9';
    if (l === 'VP8') return 'vp8';
    if (l.indexOf('ProRes') >= 0) return 'prores';
    if (l.indexOf('DNxHD') >= 0) return 'dnxhd';
    if (l.indexOf('MPEG-2') >= 0) return 'mpeg2';
    return 'other';
}
function audioCodecLabel(fmt, profile, commercial) {
    var f = (fmt || '').toLowerCase();
    if (commercial && /atmos/i.test(commercial)) return 'Dolby Atmos';
    if (f.indexOf('aac') >= 0) return 'AAC' + (profile ? ' ' + profile : '');
    if (f === 'e-ac-3' || f.indexOf('eac3') >= 0) return 'Dolby Digital Plus (E-AC-3)';
    if (f === 'ac-3' || f.indexOf('ac3') >= 0) return 'Dolby Digital (AC-3)';
    if (f.indexOf('dts') >= 0) return 'DTS' + (profile ? ' ' + profile : '');
    if (f.indexOf('opus') >= 0) return 'Opus';
    if (f.indexOf('vorbis') >= 0) return 'Vorbis';
    if (f.indexOf('flac') >= 0) return 'FLAC';
    if (f.indexOf('alac') >= 0) return 'ALAC';
    if (f.indexOf('mp3') >= 0 || f.indexOf('mpeg audio') >= 0) return 'MP3';
    if (f.indexOf('pcm') >= 0) return 'PCM';
    return fmt || '—';
}

/* ── HDR detection ── */
function detectHdr(v) {
    var hdr = field(v, 'HDR_Format', 'HDR format');
    var compat = field(v, 'HDR_Format_Compatibility', 'HDR_Format_Compatibility');
    var transfer = (field(v, 'transfer_characteristics', 'TransferCharacteristics') || '').toLowerCase();
    var primaries = (field(v, 'colour_primaries', 'ColorPrimaries', 'color_primaries') || '').toLowerCase();
    var bitDepth = num(field(v, 'BitDepth', 'BitDepth_String'));
    var type = 'SDR', isHdr = false;
    var hl = (hdr + ' ' + compat).toLowerCase();
    if (/dolby\s*vision/.test(hl)) { type = 'Dolby Vision'; isHdr = true; }
    else if (/hdr10\+|smpte\s*st\s*2094/.test(hl)) { type = 'HDR10+'; isHdr = true; }
    else if (/hdr10|smpte\s*st\s*2086|smpte2084|pq/.test(hl)) { type = 'HDR10'; isHdr = true; }
    else if (/hlg|arib|b67/.test(hl) || /hlg|b67/.test(transfer)) { type = 'HLG'; isHdr = true; }
    else if (/2084|pq/.test(transfer)) { type = 'HDR (PQ)'; isHdr = true; }
    return {
        type: type, isHdr: isHdr, raw: hdr,
        transfer: transfer, primaries: primaries, bitDepth: bitDepth,
        masteringDisplay: field(v, 'MasteringDisplay_ColorPrimaries', 'MasteringDisplay_Luminance'),
        maxCLL: field(v, 'MaxCLL'), maxFALL: field(v, 'MaxFALL'),
    };
}

/* ── build normalized report ── */
function buildReport(input) {
    input = input || {};
    var mi = input.mi || {};
    var g = mi.general || null;
    var v0 = (mi.video && mi.video[0]) || null;
    var timeline = input.timeline || null;

    var durationSec = num(field(g, 'Duration')) || (timeline && timeline.duration) || 0;
    var fileSize = input.fileSize || num(field(g, 'FileSize')) || 0;

    var report = {
        fileName: input.fileName || field(g, 'CompleteName') || '',
        container: field(g, 'Format', 'Format/String') || (input.webcodecs && input.webcodecs.container) || '',
        format: field(g, 'Format'),
        formatProfile: field(g, 'Format_Profile'),
        fileSizeBytes: fileSize,
        durationSec: durationSec,
        overallBitrateBps: num(field(g, 'OverallBitRate')),
        isStreamable: /yes/i.test(field(g, 'IsStreamable')),
        encodedDate: field(g, 'Encoded_Date', 'Tagged_Date'),
        writingApp: field(g, 'Encoded_Application', 'Writing_application', 'Encoded_Library'),
        hasVideo: !!v0,
        video: null,
        audio: [],
        text: [],
        _miAvailable: !!g,
    };

    if (v0) {
        var width = num(field(v0, 'Width')), height = num(field(v0, 'Height'));
        var hdr = detectHdr(v0);
        var fpsMode = field(v0, 'FrameRate_Mode'); // CFR / VFR
        report.video = {
            codec: videoCodecLabel(field(v0, 'Format'), field(v0, 'CodecID')),
            codecKey: videoCodecKey(field(v0, 'Format'), field(v0, 'CodecID')),
            codecId: field(v0, 'CodecID'),
            profile: field(v0, 'Format_Profile'),
            level: field(v0, 'Format_Level'),
            width: width, height: height,
            dar: field(v0, 'DisplayAspectRatio', 'DisplayAspectRatio/String'),
            sar: field(v0, 'PixelAspectRatio'),
            fps: num(field(v0, 'FrameRate')) || (timeline && timeline.fps) || NaN,
            fpsMode: fpsMode ? (/variable|vfr/i.test(fpsMode) ? 'VFR' : 'CFR') : (timeline ? null : ''),
            bitrateBps: num(field(v0, 'BitRate', 'BitRate_Nominal')),
            bitDepth: num(field(v0, 'BitDepth')),
            chroma: field(v0, 'ChromaSubsampling'),
            colorPrimaries: field(v0, 'colour_primaries', 'ColorPrimaries'),
            transfer: field(v0, 'transfer_characteristics', 'TransferCharacteristics'),
            matrix: field(v0, 'matrix_coefficients', 'MatrixCoefficients'),
            colorRange: field(v0, 'colour_range', 'ColorRange'),
            scanType: field(v0, 'ScanType'),
            rotationDeg: num(field(v0, 'Rotation')) || 0,
            refFrames: num(field(v0, 'Format_Settings_RefFrames')),
            encodedLibrary: field(v0, 'Encoded_Library_Name', 'Encoded_Library'),
            hdr: hdr,
            timeline: timeline,
        };
        if (!isFinite(report.video.bitrateBps) && timeline && timeline.avgKbps) {
            report.video.bitrateBps = timeline.avgKbps * 1000;
            report.video.bitrateEstimated = true;
        }
        // megapixels-per-second proxy used by the bitrate-efficiency heuristic
        report.video.mps = (width * height * (report.video.fps || 0)) / 1e6;
    }

    var aList = (mi.audio || []);
    for (var i = 0; i < aList.length; i++) {
        var a = aList[i];
        var loud = (input.audio && input.audio.trackIndex === i) ? input.audio : null;
        report.audio.push({
            codec: audioCodecLabel(field(a, 'Format'), field(a, 'Format_Profile'), field(a, 'Format_Commercial_IfAny', 'Format_Commercial')),
            channels: num(field(a, 'Channels')),
            channelLayout: field(a, 'ChannelLayout', 'ChannelPositions'),
            samplingRate: num(field(a, 'SamplingRate')),
            bitDepth: num(field(a, 'BitDepth')),
            bitrateBps: num(field(a, 'BitRate')),
            bitrateMode: field(a, 'BitRate_Mode'),
            language: field(a, 'Language', 'Language/String'),
            title: field(a, 'Title'),
            isDefault: /yes/i.test(field(a, 'Default')),
            lufs: loud && isFinite(loud.lufs) ? loud.lufs : null,
            truePeakDbtp: loud && isFinite(loud.truePeakDbtp) ? loud.truePeakDbtp : null,
        });
    }

    var tList = (mi.text || []);
    for (var j = 0; j < tList.length; j++) {
        var t = tList[j];
        report.text.push({
            codec: field(t, 'Format'),
            language: field(t, 'Language', 'Language/String'),
            title: field(t, 'Title'),
            isDefault: /yes/i.test(field(t, 'Default')),
            isForced: /yes/i.test(field(t, 'Forced')),
        });
    }
    return report;
}

/* ── encoding-quality verdict ──
   Heuristic target video bitrate ≈ bits-per-pixel-per-frame × W×H×fps, scaled
   per codec generation. Only STRONG deviations are flagged; the middle is
   'ideal' and grey zones are 'warn', never a confident wrong call. */
/* ── localizable verdict messages ──
   Verdicts return structured issues { level/status, code, vals, fix? } instead
   of English strings, so the page can render them in any of the 9 locales.
   MSG_EN is the built-in English fallback (keeps node/selfValidate readable);
   the blade passes a localized template table. fix = an ffmpeg command (kept
   in English — commands are universal) the user can copy to remedy the issue. */
var MSG_EN = {
    enc_unknown: 'Insufficient data (no video bitrate / resolution).',
    enc_intermediate: 'Intermediate / mezzanine codec — a high bitrate is expected (editing/archival, not delivery).',
    enc_under: 'Video bitrate :actual Mbps is well below the ~:expected Mbps typical for :codec at :w×:h@:fps — expect visible blockiness/banding.',
    enc_over: 'Video bitrate :actual Mbps is far above the ~:expected Mbps needed for :codec at :w×:h — wasted bandwidth/size for little quality gain.',
    enc_balanced: 'Video bitrate :actual Mbps is in the healthy band (~:expected Mbps expected) for :codec at :w×:h.',
    enc_estimated: 'Bitrate estimated from sample sizes (container declared none).',
    hdr_detected: 'HDR detected: :label.',
    hdr_low_bitdepth: 'Flagged HDR but only :bd-bit — genuine HDR is 10-bit+. Possibly mis-tagged or fake HDR.',
    hdr_transfer_mismatch: 'HDR metadata present but transfer is ":transfer" (not PQ/HLG) — metadata/transfer mismatch.',
    hdr10_no_mastering: 'HDR10 without mastering-display metadata — players may tone-map poorly.',
    dolby_vision_note: 'Dolby Vision uses proprietary dynamic metadata — it is lost if you re-encode without a DV-aware pipeline.',
    sdr_hdr_transfer: 'Transfer ":transfer" looks HDR but no HDR format is signalled — players may show it as SDR.',
    bt2020_8bit: 'BT.2020 primaries at :bd-bit is unusual for SDR — verify the color tags.',
    full_range: 'Full-range (0–255) levels — some players/TVs expect limited (16–235) and may crush blacks/whites.',
    codec_unsupported: 'Codec :codec is not a recommended :platform codec — re-encode to H.264/HEVC/AV1/VP9.',
    codec_pref: 'Codec :codec — :platform is most reliable with H.264 (High) or HEVC.',
    audio_pref: 'Audio :codec — :platform prefers AAC.',
    fps_over_60: 'Frame rate :fps fps exceeds 60 — :platform will resample to ≤60.',
    faststart: 'moov atom is not at the front (not faststart) — fine for upload, but not progressively streamable as-is.',
    not_mp4mov: 'Container ":container" — :platform needs MP4 or MOV.',
    ig_fps: 'Frame rate :fps fps — Instagram expects 23–60 fps.',
    ig_aspect: 'Aspect ratio :ar:1 is outside Instagram’s 9:16…1.91:1 range — it will be cropped.',
    tt_aspect: 'Not 9:16 vertical (:ar:1) — TikTok will letterbox/crop full-screen playback.',
    bc_codec: 'Codec :codec is uncommon for broadcast delivery (expect AVC/HEVC/MPEG-2/ProRes/DNxHD).',
    bc_primaries: 'Color primaries ":primaries" — broadcast SDR expects BT.709.',
    bc_loudness: 'Integrated loudness :lufs LUFS is outside the EBU R128 target (−23 ±1 LUFS).',
    bc_truepeak: 'True peak :tp dBTP exceeds the −1 dBTP ceiling.',
    hls_codec: 'Codec :codec — adaptive streaming is most compatible with H.264/HEVC/AV1.',
    hls_gop: 'Keyframe interval ~:gop s is long for HLS/DASH (2–6 s segments need keyframes ≤ segment length).',
};
/* ffmpeg remedies (English — commands are universal). :tokens interpolated. */
var FIX = {
    enc_under: 'ffmpeg -i input.mp4 -c:v libx264 -b:v :expectedM\\M -c:a copy -movflags +faststart output.mp4',
    codec_unsupported: 'ffmpeg -i input -c:v libx264 -crf 20 -preset slow -c:a aac -b:a 192k -movflags +faststart output.mp4',
    codec_pref: 'ffmpeg -i input -c:v libx264 -crf 20 -preset slow -c:a aac -b:a 192k -movflags +faststart output.mp4',
    audio_pref: 'ffmpeg -i input.mp4 -c:v copy -c:a aac -b:a 192k output.mp4',
    fps_over_60: 'ffmpeg -i input.mp4 -r 60 -c:a copy output.mp4',
    faststart: 'ffmpeg -i input.mp4 -c copy -movflags +faststart output.mp4',
    not_mp4mov: 'ffmpeg -i input -c copy output.mp4',
    ig_fps: 'ffmpeg -i input.mp4 -r 30 -c:a copy output.mp4',
    bc_loudness: 'ffmpeg -i input.mp4 -af loudnorm=I=-23:TP=-1:LRA=11 -c:v copy output.mp4',
    bc_truepeak: 'ffmpeg -i input.mp4 -af loudnorm=I=-23:TP=-1:LRA=11 -c:v copy output.mp4',
    hls_gop: 'ffmpeg -i input.mp4 -c:v libx264 -g 48 -keyint_min 48 -sc_threshold 0 -c:a copy -movflags +faststart output.mp4',
};
function fmt(tpl, vals) {
    return String(tpl).replace(/:([a-zA-Z]\w*)/g, function (m, k) { return (vals && vals[k] != null) ? String(vals[k]) : m; });
}
function formatIssue(issue, table) {
    if (!issue) return '';
    if (typeof issue === 'string') return issue;
    var t = (table && table[issue.code]) || MSG_EN[issue.code] || issue.code;
    return fmt(t, issue.vals);
}
function fixFor(issue) {
    if (!issue || !issue.code || !FIX[issue.code]) return '';
    return fmt(FIX[issue.code], issue.vals).replace('\\M', 'M');
}

var BPP = { h264: 0.090, hevc: 0.050, av1: 0.045, vp9: 0.055, vp8: 0.090, mpeg2: 0.150, prores: 0.700, dnxhd: 0.600, other: 0.080 };
function encodingVerdict(report) {
    var v = report.video;
    if (!v || !v.width || !v.height || !isFinite(v.bitrateBps) || v.bitrateBps <= 0) {
        return { status: 'unknown', label: 'unknown', reasons: [{ code: 'enc_unknown' }] };
    }
    if (v.codecKey === 'prores' || v.codecKey === 'dnxhd') {
        return { status: 'pass', label: 'intermediate', reasons: [{ code: 'enc_intermediate' }] };
    }
    var fps = v.fps && isFinite(v.fps) ? v.fps : 30;
    var bpp = BPP[v.codecKey] || BPP.other;
    var expected = bpp * v.width * v.height * fps; // bps
    var ratio = v.bitrateBps / expected;
    var reasons = [];
    var vals = { actual: (v.bitrateBps / 1e6).toFixed(1), expected: (expected / 1e6).toFixed(1), expectedM: Math.max(1, Math.round(expected / 1e6)), codec: v.codec, w: v.width, h: v.height, fps: Math.round(fps) };
    var status, label;
    if (ratio < 0.4) { status = 'warn'; label = 'under-bitrated'; reasons.push({ code: 'enc_under', vals: vals }); }
    else if (ratio > 2.6) { status = 'warn'; label = 'over-bitrated'; reasons.push({ code: 'enc_over', vals: vals }); }
    else { status = 'pass'; label = 'well-balanced'; reasons.push({ code: 'enc_balanced', vals: vals }); }
    if (v.bitrateEstimated) reasons.push({ code: 'enc_estimated' });
    return { status: status, label: label, reasons: reasons, expectedBps: expected, ratio: ratio };
}

/* ── HDR / color consistency ── */
function colorHdrChecks(report) {
    var out = [], v = report.video;
    if (!v) return out;
    var h = v.hdr, transfer = (h.transfer || '').toLowerCase(), bd = h.bitDepth;
    if (h.isHdr) {
        out.push({ level: 'info', code: 'hdr_detected', vals: { label: h.type + (h.raw ? ' (' + h.raw + ')' : '') } });
        if (isFinite(bd) && bd < 10) out.push({ level: 'warn', code: 'hdr_low_bitdepth', vals: { bd: bd } });
        if (transfer && !/2084|pq|hlg|b67|2100/.test(transfer)) out.push({ level: 'warn', code: 'hdr_transfer_mismatch', vals: { transfer: v.transfer } });
        if (h.type === 'HDR10' && !h.masteringDisplay) out.push({ level: 'warn', code: 'hdr10_no_mastering' });
        if (h.type === 'Dolby Vision') out.push({ level: 'info', code: 'dolby_vision_note' });
    } else {
        if (/2084|pq|hlg|b67/.test(transfer)) out.push({ level: 'warn', code: 'sdr_hdr_transfer', vals: { transfer: v.transfer } });
        if (/2020/.test((v.colorPrimaries || '').toLowerCase()) && isFinite(bd) && bd < 10) out.push({ level: 'warn', code: 'bt2020_8bit', vals: { bd: bd } });
    }
    if (v.colorRange && /full/i.test(v.colorRange)) out.push({ level: 'info', code: 'full_range' });
    return out;
}

/* ── platform-readiness ──
   Spec-derived rules. status: 'pass' | 'warn' | 'fail'. Each issue is a
   concrete, fixable note. We intentionally err to 'warn' (not 'fail') for
   soft/recommended limits, 'fail' only for hard incompatibilities. */
function _container(report) { return (report.container || '').toLowerCase(); }
function _aspect(v) { return v && v.width && v.height ? v.width / v.height : 0; }
function platformVerdicts(report) {
    var v = report.video, out = [];
    var cont = _container(report), aud = report.audio[0];
    var codec = v ? v.codecKey : '', fps = v ? v.fps : 0, ar = _aspect(v);
    var audCodec = aud ? aud.codec.toLowerCase() : '';
    var notMp4Mov = cont && cont.indexOf('mpeg-4') < 0 && cont.indexOf('quicktime') < 0 && cont.indexOf('mp4') < 0 && cont.indexOf('mov') < 0;

    /* Lazy messages: the msg arg is a function evaluated ONLY when the rule
       fires, so interpolating a possibly-null number never throws. */
    function verdict(platform, build) {
        var issues = [], status = 'pass';
        function fire(cond, fn, level) {
            if (!cond) return;
            issues.push(typeof fn === 'function' ? fn() : fn);
            if (level === 'fail') status = 'fail';
            else if (status !== 'fail') status = 'warn';
        }
        build(
            function warn(cond, fn){ fire(cond, fn, 'warn'); },
            function fail(cond, fn){ fire(cond, fn, 'fail'); }
        );
        out.push({ platform: platform, status: status, issues: issues });
    }

    // YouTube — very permissive; recommends H.264 High / AAC-LC, faststart.
    verdict('YouTube', function(warn, fail){
        fail(v && codec !== '' && ['h264','hevc','av1','vp9','vp8','mpeg2'].indexOf(codec) < 0,
             function(){ return { code: 'codec_unsupported', vals: { codec: v.codec, platform: 'YouTube' } }; });
        warn(aud && audCodec !== '' && audCodec.indexOf('aac') < 0 && audCodec.indexOf('opus') < 0 && audCodec.indexOf('—') < 0,
             function(){ return { code: 'audio_pref', vals: { codec: aud.codec, platform: 'YouTube' } }; });
        warn(v && isFinite(fps) && fps > 60.5,
             function(){ return { code: 'fps_over_60', vals: { fps: Math.round(fps), platform: 'YouTube' } }; });
        warn(!!report.video && !report.isStreamable, function(){ return { code: 'faststart' }; });
    });

    // Instagram Feed/Reels — MP4/MOV, H.264 (or HEVC), AAC, 23–60fps.
    verdict('Instagram', function(warn, fail){
        fail(notMp4Mov, function(){ return { code: 'not_mp4mov', vals: { container: report.container, platform: 'Instagram' } }; });
        warn(v && codec !== '' && ['h264','hevc'].indexOf(codec) < 0,
             function(){ return { code: 'codec_pref', vals: { codec: v.codec, platform: 'Instagram' } }; });
        warn(aud && audCodec !== '' && audCodec.indexOf('aac') < 0 && audCodec.indexOf('—') < 0,
             function(){ return { code: 'audio_pref', vals: { codec: aud.codec, platform: 'Instagram' } }; });
        warn(v && isFinite(fps) && (fps > 60.5 || fps < 23),
             function(){ return { code: 'ig_fps', vals: { fps: Math.round(fps) } }; });
        warn(!!(v && ar) && (ar < 0.5 || ar > 1.92),
             function(){ return { code: 'ig_aspect', vals: { ar: ar.toFixed(2) } }; });
    });

    // TikTok — MP4/MOV, H.264/HEVC, vertical 9:16 recommended, ≤60fps.
    verdict('TikTok', function(warn, fail){
        fail(notMp4Mov, function(){ return { code: 'not_mp4mov', vals: { container: report.container, platform: 'TikTok' } }; });
        warn(v && codec !== '' && ['h264','hevc'].indexOf(codec) < 0,
             function(){ return { code: 'codec_pref', vals: { codec: v.codec, platform: 'TikTok' } }; });
        warn(!!(v && ar) && Math.abs(ar - (9/16)) > 0.08,
             function(){ return { code: 'tt_aspect', vals: { ar: ar.toFixed(2) } }; });
        warn(v && isFinite(fps) && fps > 60.5,
             function(){ return { code: 'fps_over_60', vals: { fps: Math.round(fps), platform: 'TikTok' } }; });
    });

    // Broadcast / EBU R128 — codec + BT.709 (SDR) + loudness ≈ -23 LUFS, TP ≤ -1 dBTP.
    verdict('Broadcast (EBU R128)', function(warn, fail){
        warn(v && codec !== '' && ['h264','hevc','mpeg2','prores','dnxhd'].indexOf(codec) < 0,
             function(){ return { code: 'bc_codec', vals: { codec: v.codec } }; });
        warn(!!(v && !v.hdr.isHdr && v.colorPrimaries) && !/709|601|2020/i.test(v.colorPrimaries),
             function(){ return { code: 'bc_primaries', vals: { primaries: v.colorPrimaries } }; });
        warn(!!(aud && aud.lufs != null) && Math.abs(aud.lufs - (-23)) > 1,
             function(){ return { code: 'bc_loudness', vals: { lufs: aud.lufs.toFixed(1) } }; });
        warn(!!(aud && aud.truePeakDbtp != null) && aud.truePeakDbtp > -1,
             function(){ return { code: 'bc_truepeak', vals: { tp: aud.truePeakDbtp.toFixed(1) } }; });
    });

    // HLS/DASH streaming — H.264/HEVC/AV1, short GOP (≤10s), faststart.
    var gopSec = v && v.timeline && v.timeline.gop ? v.timeline.gop.meanSec : NaN;
    verdict('HLS / DASH streaming', function(warn, fail){
        warn(v && codec !== '' && ['h264','hevc','av1'].indexOf(codec) < 0,
             function(){ return { code: 'hls_codec', vals: { codec: v.codec } }; });
        warn(isFinite(gopSec) && gopSec > 10,
             function(){ return { code: 'hls_gop', vals: { gop: gopSec.toFixed(1) } }; });
        warn(!!report.video && !report.isStreamable, function(){ return { code: 'faststart' }; });
    });

    return out;
}

/* ── headline summary ── */
function summary(report) {
    var v = report.video, badges = [];
    if (!v) return { headline: report.container ? (report.container + ' file — no video track') : 'No video track', badges: [], status: 'warn' };
    badges.push(v.codec);
    if (v.width && v.height) badges.push(v.width + '×' + v.height);
    if (isFinite(v.fps)) badges.push(Math.round(v.fps * 100) / 100 + ' fps' + (v.fpsMode ? ' ' + v.fpsMode : ''));
    if (v.hdr.isHdr) badges.push(v.hdr.type);
    else badges.push('SDR');
    if (isFinite(v.bitDepth)) badges.push(v.bitDepth + '-bit');
    var plats = platformVerdicts(report);
    var anyFail = plats.some(function(p){ return p.status === 'fail'; });
    var anyWarn = plats.some(function(p){ return p.status === 'warn'; });
    var status = anyFail ? 'fail' : (anyWarn ? 'warn' : 'pass');
    var headline = v.codec + ' · ' + (v.width && v.height ? v.width + '×' + v.height : '?') + (v.hdr.isHdr ? ' · ' + v.hdr.type : '');
    return { headline: headline, badges: badges, status: status };
}

/* ── self-validation (synthetic scenarios) ── */
function selfValidate() {
    var failures = [], total = 0;
    function check(name, cond) { total++; if (!cond) failures.push(name); }

    // 1080p30 H.264 at 0.4 Mbps → under-bitrated
    var underReport = buildReport({ mi: { general: { Format: 'MPEG-4', Duration: '60' }, video: [{ Format: 'AVC', Width: '1920', Height: '1080', FrameRate: '30', BitRate: '400000' }], audio: [] } });
    check('under-bitrated detected', encodingVerdict(underReport).label === 'under-bitrated');

    // 1080p30 H.264 at 8 Mbps → well-balanced
    var okReport = buildReport({ mi: { general: { Format: 'MPEG-4' }, video: [{ Format: 'AVC', Width: '1920', Height: '1080', FrameRate: '30', BitRate: '8000000' }], audio: [] } });
    check('balanced bitrate detected', encodingVerdict(okReport).label === 'well-balanced');

    // HDR10 PQ 10-bit → HDR consistent (no mismatch warn)
    var hdrReport = buildReport({ mi: { general: { Format: 'MPEG-4' }, video: [{ Format: 'HEVC', Width: '3840', Height: '2160', FrameRate: '24', BitRate: '40000000', BitDepth: '10', HDR_Format: 'SMPTE ST 2086, HDR10', transfer_characteristics: 'PQ', colour_primaries: 'BT.2020', MasteringDisplay_ColorPrimaries: 'Display P3', MasteringDisplay_Luminance: 'min: 0.0050 cd/m2, max: 1000 cd/m2', MaxCLL: '1000 cd/m2', MaxFALL: '400 cd/m2' }], audio: [] } });
    var hdrChecks = colorHdrChecks(hdrReport);
    check('HDR10 detected', hdrReport.video.hdr.type === 'HDR10' && hdrReport.video.hdr.isHdr);
    check('HDR10 no mismatch warn', !hdrChecks.some(function(c){ return c.level === 'warn'; }));

    // Fake HDR: HDR_Format but 8-bit + bt709 transfer → warn
    var fakeHdr = buildReport({ mi: { general: {}, video: [{ Format: 'AVC', Width: '1920', Height: '1080', BitDepth: '8', HDR_Format: 'HDR10', transfer_characteristics: 'BT.709' }], audio: [] } });
    check('fake HDR flagged', colorHdrChecks(fakeHdr).some(function(c){ return c.level === 'warn'; }));

    // Vertical 1080x1920 H.264 → TikTok pass (9:16)
    var vert = buildReport({ mi: { general: { Format: 'MPEG-4' }, video: [{ Format: 'AVC', Width: '1080', Height: '1920', FrameRate: '30', BitRate: '6000000' }], audio: [{ Format: 'AAC', Channels: '2' }] } });
    var tiktok = platformVerdicts(vert).find(function(p){ return p.platform === 'TikTok'; });
    check('vertical passes TikTok', tiktok && tiktok.status !== 'fail');

    // Landscape 16:9 → TikTok warn (not 9:16)
    var land = buildReport({ mi: { general: { Format: 'MPEG-4' }, video: [{ Format: 'AVC', Width: '1920', Height: '1080', FrameRate: '30', BitRate: '8000000' }], audio: [{ Format: 'AAC', Channels: '2' }] } });
    var tiktok2 = platformVerdicts(land).find(function(p){ return p.platform === 'TikTok'; });
    check('landscape warns on TikTok', tiktok2 && tiktok2.status === 'warn');

    // AAC audio at -23 LUFS, TP -2 → broadcast pass
    var bcast = buildReport({ mi: { general: { Format: 'MPEG-4' }, video: [{ Format: 'AVC', Width: '1920', Height: '1080', FrameRate: '25', BitRate: '8000000', colour_primaries: 'BT.709' }], audio: [{ Format: 'AAC', Channels: '2' }] }, audio: { trackIndex: 0, lufs: -23, truePeakDbtp: -2 } });
    var br = platformVerdicts(bcast).find(function(p){ return p.platform === 'Broadcast (EBU R128)'; });
    check('broadcast loudness pass', br && br.status === 'pass');

    // Loud master -9 LUFS → broadcast warn
    var loud = buildReport({ mi: { general: { Format: 'MPEG-4' }, video: [{ Format: 'AVC', Width: '1920', Height: '1080', FrameRate: '25', BitRate: '8000000', colour_primaries: 'BT.709' }], audio: [{ Format: 'AAC', Channels: '2' }] }, audio: { trackIndex: 0, lufs: -9, truePeakDbtp: -0.2 } });
    var br2 = platformVerdicts(loud).find(function(p){ return p.platform === 'Broadcast (EBU R128)'; });
    check('broadcast loudness warn', br2 && br2.status === 'warn');

    // ProRes → encoding verdict intermediate (not under/over)
    var prores = buildReport({ mi: { general: { Format: 'QuickTime' }, video: [{ Format: 'ProRes', Width: '1920', Height: '1080', FrameRate: '25', BitRate: '147000000' }], audio: [] } });
    check('prores intermediate', encodingVerdict(prores).label === 'intermediate');

    return { passed: total - failures.length, total: total, failures: failures };
}

var api = {
    version: '1.1.0',
    field: field, num: num,
    videoCodecLabel: videoCodecLabel, audioCodecLabel: audioCodecLabel, detectHdr: detectHdr,
    buildReport: buildReport,
    encodingVerdict: encodingVerdict,
    colorHdrChecks: colorHdrChecks,
    platformVerdicts: platformVerdicts,
    summary: summary,
    formatIssue: formatIssue, fixFor: fixFor, MSG_EN: MSG_EN,
    selfValidate: selfValidate,
};

if (typeof module !== 'undefined' && module.exports) module.exports = api;
if (root) root.VideoAnalyzerEngine = api;

})(typeof window !== 'undefined' ? window : null);
